import { useCallback, useEffect, useRef } from 'react'

import {
  AlbumGate,
  AlbumTypeTrackTypeMatchEnum,
  albumByIdQuery,
} from '@/entities/albums'
import { type AuthorPreview } from '@/entities/authors'
import { ROUTES } from '@/shared/config'
import {
  ContentWrapper,
  ErrorResult,
  HeaderContent,
  HidingSpin,
} from '@/shared/ui'
import { AlbumForm } from '@/widgets/AlbumForm'
import { AuthorForm, authorFormModalOpened } from '@/widgets/AuthorForm'
import {
  TrackForm,
  trackFormModalClosed,
  trackFormModalOpened,
} from '@/widgets/TrackForm'
import { Breadcrumb, Layout } from 'antd'
import { useUnit } from 'effector-react'
import { Link, useLocation, useParams } from 'react-router-dom'

export const AlbumEditPage = () => {
  const { id } = useParams<{ id: string }>()
  const album = useUnit(albumByIdQuery)

  const location = useLocation()
  const state = location.state as {
    author?: AuthorPreview
  }
  const author = state?.author

  const openAuthorsFormModal = useUnit(authorFormModalOpened)

  const onAuthorSubmitted = useRef<(id: string) => void>(() => {})
  const onAuthorButtonClick = useCallback(
    (callback: (id: string) => void) => {
      onAuthorSubmitted.current = callback
      openAuthorsFormModal()
    },
    [openAuthorsFormModal],
  )

  const openTracksFormModal = useUnit(trackFormModalOpened)
  const closeTracksFormModal = useUnit(trackFormModalClosed)
  const onTrackEditButtonClick = useCallback(
    (id: string) => {
      void openTracksFormModal({ id })
    },
    [openTracksFormModal],
  )

  useEffect(() => {
    return () => closeTracksFormModal()
  }, [closeTracksFormModal])

  return (
    <Layout>
      {id && <AlbumGate id={id} />}
      <AuthorForm
        isModal
        onSubmitted={(authorId: string) => onAuthorSubmitted.current(authorId)}
      />
      <TrackForm
        isModal
        trackTypes={
          album.data?.type !== undefined
            ? [AlbumTypeTrackTypeMatchEnum[album.data.type]]
            : []
        }
        initialAuthor={
          album.data?.authors
            ?.filter((a) => !a.isCoauthor)
            .map((a) => ({ id: a.id, nickname: a.nickname }))[0]
        }
      />
      <HidingSpin spinning={album.pending || (!album.data && !album.error)}>
        {album.data && !album.error ? (
          <>
            <HeaderContent
              breadcrumbSlot={
                <Breadcrumb
                  items={[
                    ...(author
                      ? [
                          { title: 'Авторы' },
                          {
                            title: (
                              <Link
                                to={ROUTES.AUTHORS.EDIT.replace(
                                  ':id',
                                  author.id,
                                )}
                              >
                                {author.nickname}
                              </Link>
                            ),
                          },
                        ]
                      : [
                          { title: 'Контент' },
                          { title: 'Коллекции' },
                          {
                            title: (
                              <Link to={ROUTES.COLLECTIONS.ALBUMS}>
                                Альбомы
                              </Link>
                            ),
                          },
                        ]),
                    {
                      title: album.data.name,
                    },
                  ]}
                />
              }
              title="Редактирование альбома"
              description="Раздел предоставляет возможность добавлять новый контент в каталог и управлять его параметрами"
            />

            <ContentWrapper>
              <AlbumForm
                onAuthorButtonClick={onAuthorButtonClick}
                onTrackEditButtonClick={onTrackEditButtonClick}
              />
            </ContentWrapper>
          </>
        ) : (
          <ContentWrapper>
            <ErrorResult
              error={album.error}
              notFoundConfig={{
                subTitle: 'Данного альбома не существует.',
                navigateTo: ROUTES.COLLECTIONS.ALBUMS,
              }}
            />
          </ContentWrapper>
        )}
      </HidingSpin>
    </Layout>
  )
}
